import {
  Alert,
  Button,
  Descriptions,
  Layout,
  Menu,
  Modal,
  Spin,
  Typography,
} from 'antd';
import {emptySplitApi} from 'api/apiSlice';
import {useGetMeQuery, useLogoutMutation} from 'api/authSlice';
import {axiosInstance} from 'api/axiosFuncs';
import isEqual from 'fast-deep-equal';
import {useTokenRefresh} from 'hooks/useTokenRefresh';
import {SESSION_EXPIRED_MESSAGE_KEY} from 'middleware/errorHandler';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, Outlet, useLocation} from 'react-router-dom';
import logo from 'res/img/Main_Logo_RGB_white_2x.webp';
import {persistor} from 'store';
import {
  parsePermissions,
  resetAuth,
  selectEmployeeId,
  selectIsBootstrapped,
  selectIsLoggedIn,
  selectPermissions,
  selectPsSiteCodes,
  selectUser,
  setAuthData,
  setBootstrapped,
} from 'store/authSlice';
import {
  clearSwitchingAccount,
  markAutoLoginAttempt,
  markSwitchingAccount,
} from 'utils/autoLogin';
import {HIDDEN_TABS, SWITCH_USER_URL} from 'utils/consts';
import {buildSupportEmailMessage, sendSupportEmail} from 'utils/contactIT';
import {setDatadogUser} from 'utils/datadog';
import {isUnsupportedBrowser} from 'utils/environment';
import {LOGIN_SUCCESS_EVENT, openLoginPopup} from 'utils/loginPopup';
import {messageApi, toast} from 'utils/toast';

const DEFAULT_MESSAGE_TOP = 8; // antd's default message.config top offset

const {Header, Content, Footer} = Layout;
const {Text} = Typography;

const TrackLayout = ({simplified = false}) => {
  const location = useLocation();
  const selectedKeys = location.pathname.split('/')?.[1];
  const dispatch = useDispatch();
  const [logout] = useLogoutMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBrowserWarning, setShowBrowserWarning] = useState(
    isUnsupportedBrowser()
  );
  const browserWarningRef = useRef(null);

  const permissions = useSelector(selectPermissions);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isBootstrapped = useSelector(selectIsBootstrapped);
  const user = useSelector(selectUser);
  const psSiteCodes = useSelector(selectPsSiteCodes);
  const employeeId = useSelector(selectEmployeeId);

  const updateAuth = useCallback(
    (data, perms = parsePermissions(data)) => {
      dispatch(setAuthData({data, permissions: perms}));
      clearSwitchingAccount();
    },
    [dispatch]
  );

  useEffect(() => {
    // Guarantees Datadog has a user whenever someone is logged in, including
    // on a fresh page load where auth state is rehydrated from persisted
    // storage and /auth/me/ returns unchanged permissions (no updateAuth call).
    if (!user) return;
    setDatadogUser({
      user_info: user,
      employee_id: employeeId,
      ps_site_codes: psSiteCodes,
      permissions: {...permissions},
    });
  }, [user, permissions, psSiteCodes, employeeId]);

  useEffect(() => {
    const handleLoginSuccess = (event) => {
      toast.destroy(SESSION_EXPIRED_MESSAGE_KEY);
      if (event.detail?.data) {
        updateAuth(event.detail.data);
      }
      dispatch(emptySplitApi.util.resetApiState());
    };
    window.addEventListener(LOGIN_SUCCESS_EVENT, handleLoginSuccess);
    return () =>
      window.removeEventListener(LOGIN_SUCCESS_EVENT, handleLoginSuccess);
  }, [dispatch, updateAuth]);

  useEffect(() => {
    setIsModalOpen(false);
  }, [location.pathname]);

  useTokenRefresh();

  // Push antd's global `message` popups (used for session-expired/auth
  // prompts) below the browser warning banner so they don't cover it.
  useEffect(() => {
    if (!showBrowserWarning) {
      messageApi.config({top: DEFAULT_MESSAGE_TOP});
      return;
    }
    const el = browserWarningRef.current?.nativeElement;
    if (!el) return;

    const updateOffset = () => {
      messageApi.config({top: el.offsetHeight + DEFAULT_MESSAGE_TOP});
    };
    updateOffset();

    const observer = new ResizeObserver(updateOffset);
    observer.observe(el);
    return () => {
      observer.disconnect();
      messageApi.config({top: DEFAULT_MESSAGE_TOP});
    };
  }, [showBrowserWarning]);

  const isPopup = Boolean(
    window.opener && !window.opener.closed && window.opener !== window
  );

  // On the OAuth callback routes the session cookie isn't set until
  // <LoginDone> exchanges the `code`. Firing /auth/me/ here only races that
  // exchange and always 401s, so skip it — the post-exchange hard navigation
  // re-runs the bootstrap query with a valid session.
  const isAuthCallbackRoute =
    /\/login(_done|_failed)\/?$/.test(location.pathname) ||
    new URLSearchParams(location.search).has('code');

  const {
    data: meData,
    isError: meError,
    error: meRequestError,
  } = useGetMeQuery(undefined, {
    skip: isPopup || isAuthCallbackRoute,
    pollingInterval: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!meData) return;
    if (!isLoggedIn) {
      // Bootstrap: hydrate auth state from a valid cookie session
      updateAuth(meData);
      return;
    }
    // Already logged in: sync permissions or site codes if they changed
    const freshPerms = parsePermissions(meData);
    const permissionsChanged = Object.keys(freshPerms).some(
      (key) => freshPerms[key] !== permissions[key]
    );
    const siteCodesChanged = !isEqual(meData.ps_site_codes, psSiteCodes);
    if (permissionsChanged || siteCodesChanged) {
      updateAuth(meData, freshPerms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meData]);

  useEffect(() => {
    const status = meRequestError?.status ?? meRequestError?.originalStatus;
    const isAuthFailure = status === 401 || status === 403;

    // If persisted auth rehydrates but /auth/me/ rejects, clear the stale auth
    // state without redirecting so route guards can show the correct login UX.
    if (meError && isAuthFailure && isLoggedIn) {
      dispatch(resetAuth());
      return;
    }

    // Bootstrap failed while already unauthenticated — mark bootstrapped so
    // ProtectedRoute can show the login prompt.
    if (meError && !isBootstrapped) {
      dispatch(setBootstrapped());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meError, meRequestError, isLoggedIn, isBootstrapped]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (err) {
      // 401 means the session was already expired — logout goal is achieved, nothing to report
      if (err?.status !== 401) {
        toast.error('Error logging out from server, but local session cleared');
      }
    }
    // resetAuth() marks bootstrapped immediately, and this component is still
    // mounted on Home. Without this, TrackHome's automatic-login effect can
    // react before the reload below fires and redirect to a live OneLogin
    // session instead of a clean logged-out Home page.
    markAutoLoginAttempt();
    // Stop persisting first: otherwise the reset state (bootstrapped, no user)
    // is written back after purge(), and the reloaded page treats itself as
    // logged out before /auth/me/ has answered.
    persistor.pause();
    dispatch(resetAuth());
    await persistor.purge();
    window.location.href = '/';
  };

  const handleSwitchAccount = async () => {
    let res;
    try {
      res = await axiosInstance.post(SWITCH_USER_URL);
    } catch (err) {
      if (err?.response?.status === 403) {
        dispatch(resetAuth());
        await persistor.purge();
        window.location.href = '/login';
      } else {
        toast.error('Unable to switch account. Please try again.');
      }
      return;
    }
    const {ol_logout_url, auth_url, state} = res.data;

    sessionStorage.setItem('redirect', window.location.pathname);
    if (state) {
      sessionStorage.setItem('oauth_state', state);
    }
    markSwitchingAccount();
    dispatch(resetAuth());
    await persistor.purge();

    await new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = ol_logout_url;
      const timeout = setTimeout(resolve, 3000);
      iframe.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
      document.body.appendChild(iframe);
    });

    window.location.assign(auth_url);
  };

  const contactIT = () => {
    const email = buildSupportEmailMessage({
      subject: 'TRACK Support',
      user: {...user, ps_site_codes: psSiteCodes, permissions},
    });
    sendSupportEmail(email);
  };

  const showUserDetails = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // The menu items to display.
  const isLoggedInItems = [
    {
      key: 'home',
      label: <Link to="/">Home</Link>,
    },
    ...(permissions.access_to_audits
      ? [
          {
            key: 'audit',
            label: 'Audits',
            children: [
              {
                key: 'audits_overview',
                label: <Link to="/audits_overview">Audits Overview</Link>,
              },
              {
                key: 'audits_issue_overview',
                label: (
                  <Link to="/audits_issue_overview">Audits Issue Overview</Link>
                ),
              },
              {
                key: 'audits_school_summary',
                label: (
                  <Link to="/audits_school_summary">Audits School Summary</Link>
                ),
              },
              {
                key: 'audits',
                label: <Link to="/audits">Audits Table</Link>,
              },
              {
                key: 'audit_rules',
                label: <Link to="/audit_rules">Audit Rules</Link>,
              },
            ],
          },
        ]
      : []),
    ...(permissions.access_to_esoy && !HIDDEN_TABS.includes('school_setup')
      ? [
          {
            key: 'school_setup',
            label: <Link to="/school_setup">E/SOY</Link>,
          },
        ]
      : []),
    ...(permissions.can_view_esoy_overview
      ? [
          {
            key: 'esoy_it_support',
            label: <Link to="/esoy_it_support">E/SOY (IT Support)</Link>,
          },
        ]
      : []),
    ...(permissions.access_to_data_reports &&
    !HIDDEN_TABS.includes('data_report')
      ? [
          {
            key: 'data_report',
            label: <Link to="/data_report">Data Reports</Link>,
          },
        ]
      : []),
    ...(permissions.access_to_source_manager &&
    !HIDDEN_TABS.includes('source_manager')
      ? [
          {
            key: 'source_manager',
            label: <Link to="/source_manager">Source Manager</Link>,
          },
        ]
      : []),
    ...(permissions.access_to_data_portal && !HIDDEN_TABS.includes('editorjs')
      ? [
          {
            key: 'editorjs',
            label: <Link to="/editorjs">Data Portal (EditorJS)</Link>,
          },
        ]
      : []),
    ...(permissions.access_to_staff_account_status &&
    !HIDDEN_TABS.includes('staff_roles')
      ? [
          {
            key: 'staff_roles',
            label: <Link to="/staff_roles">Staff Accounts</Link>,
          },
        ]
      : []),
    ...(permissions.access_to_staff_account_status &&
    permissions.can_view_esoy_overview
      ? [
          {
            key: 'staff_roles_it_support',
            label: (
              <Link to="/staff_roles_it_support">
                Staff Accounts (IT Support)
              </Link>
            ),
          },
        ]
      : []),
    ...(permissions.access_to_hs_schedule &&
    !HIDDEN_TABS.includes('hs_schedule')
      ? [
          {
            key: 'hs_schedule',
            label: <Link to="/hs_schedule">HS Scheduling</Link>,
          },
        ]
      : []),
  ];

  const items = isLoggedIn
    ? isLoggedInItems
    : [
        {
          key: 'home',
          label: <Link to="/">Home</Link>,
        },
      ];

  const userItems = [
    ...(isLoggedIn
      ? simplified
        ? [
            {
              key: 'contact_it',
              label: 'Contact IT Support',
              onClick: contactIT,
            },
            {
              key: 'logout',
              label: 'Logout',
              onClick: handleLogout,
            },
          ]
        : [
            {
              key: 'user_details',
              label: user?.name,
              children: [
                {
                  key: 'view_details',
                  label: 'User Details',
                  onClick: showUserDetails,
                },
                {
                  key: 'divider_1',
                  type: 'divider',
                },
                {
                  key: 'contact_it',
                  label: 'Contact IT Support',
                  onClick: contactIT,
                },
                {
                  key: 'divider_2',
                  type: 'divider',
                },
                {
                  key: 'switch_account',
                  label: 'Switch Account',
                  onClick: handleSwitchAccount,
                },
                {
                  key: 'logout',
                  label: 'Logout',
                  onClick: handleLogout,
                },
              ],
            },
          ]
      : [
          {
            key: 'login',
            label: simplified ? (
              <Link to="/login">Login</Link>
            ) : (
              <span onClick={openLoginPopup} style={{cursor: 'pointer'}}>
                Login
              </span>
            ),
          },
        ]),
  ];

  const userDescriptionItems = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        children: <Text>{user?.name}</Text>,
      },
      {
        key: 'email',
        label: 'Email',
        children: <Text>{user?.email}</Text>,
      },
      {
        key: 'ps_site_codes',
        label: 'ps_site_codes',
        children: <Text>{psSiteCodes.join(', ')}</Text>,
      },
      {
        key: 'onelogin_id',
        label: 'onelogin_id',
        children: <Text>{user?.sub}</Text>,
      },
      {
        key: 'employee_id',
        label: 'employee_id',
        children: <Text>{employeeId}</Text>,
      },
      {
        key: 'uncommonrole',
        label: 'uncommonrole',
        children: <Text>{user?.uncommonrole}</Text>,
      },
      {
        key: 'location_wid',
        label: 'location_wid',
        children: <Text>{user?.location_wid}</Text>,
      },
      {
        key: 'permissions',
        label: 'Permissions',
        children: (
          <Text>
            {Object.entries(permissions)?.map(([key, value]) => (
              <div key={key}>
                <strong>{key}:</strong> {String(value)}
              </div>
            ))}
          </Text>
        ),
      },
    ],
    [user, psSiteCodes, employeeId, permissions]
  );

  return (
    <Layout className="track-layout">
      {showBrowserWarning && (
        <Alert
          ref={browserWarningRef}
          banner
          type="warning"
          closable
          onClose={() => setShowBrowserWarning(false)}
          message="TRACK does not support Safari or mobile browsers. Some features may not work correctly. For the best experience, use Chrome or Edge on a desktop."
        />
      )}
      <Header className="header">
        <img src={logo} alt="TRACK" className="logo" width={100} height={40} />
        {simplified ? (
          <div style={{flex: 1}} />
        ) : (
          <Menu
            theme="dark"
            mode="horizontal"
            defaultSelectedKeys={['home']}
            selectedKeys={selectedKeys}
            items={items}
            style={{
              flexGrow: 1,
              minWidth: 50,
            }}
          />
        )}
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={selectedKeys}
          items={userItems}
          style={{
            flexShrink: 0,
            flexGrow: 2,
            justifyContent: 'flex-end',
          }}
        />
      </Header>
      <Content
        style={
          simplified
            ? {
                padding: '0 50px',
                marginTop: '20px',
                minHeight: 'calc(100vh - 84px)',
              }
            : {padding: '0 50px'}
        }
      >
        <Suspense
          fallback={
            <div
              style={{
                textAlign: 'center',
                padding: '48px 0',
                minBlockSize: 'calc(100vh - 84px)',
              }}
            >
              <Spin size="large" />
            </div>
          }
        >
          {simplified ? (
            <Outlet />
          ) : (
            <div className="site-layout-content">
              <Outlet />
            </div>
          )}
        </Suspense>
      </Content>
      {!simplified && (
        <Footer>
          <div style={{textAlign: 'center'}}>
            TRACK - Tracker Replacement Automated Correction Kit
          </div>
        </Footer>
      )}

      {!simplified && (
        <Modal
          destroyOnHidden={true} // This helps clean up memory
          footer={null}
          onCancel={handleModalClose}
          open={isModalOpen}
          title={
            <div>
              <Text strong style={{fontSize: 16}}>
                User Details
              </Text>
              <Button
                onClick={() => {
                  const userDetails = {
                    name: user?.name,
                    email: user?.email,
                    ps_site_codes: psSiteCodes,
                    onelogin_id: user?.sub,
                    employee_id: employeeId,
                    uncommonrole: user?.uncommonrole,
                    location_wid: user?.location_wid,
                    permissions: {...permissions},
                  };
                  navigator.clipboard.writeText(
                    JSON.stringify(userDetails, null, 2)
                  );
                }}
                style={{marginLeft: '16px'}}
              >
                Copy to Clipboard
              </Button>
            </div>
          }
          width={500}
        >
          <Descriptions
            column={1}
            bordered
            size="small"
            items={userDescriptionItems}
          />
        </Modal>
      )}
    </Layout>
  );
};

export default TrackLayout;
